import React, { forwardRef, useEffect, useRef, useState } from "react";
import "./style.css";

import cobbLogo from "@/assets/images/logos/cobb/cobb_icon.png";
import { useFirebaseAuth } from "@/contexts/UserProfileContext/UserProfileContext";
import { getFirestoreDB } from "@/firebase";
import { BetaSurveyDismissed, BetaSurveyPopupDismissed } from "@/utils/localStorageWrapper";
import { useUserOrganization } from "@/utils/partnerData";
import { clearUrlFlag, getBooleanUrlFlag } from "@/utils/urlFlags";
import {
    BugOutlined,
    CloseOutlined,
    CommentOutlined,
    FileOutlined,
    FrownOutlined,
    MehOutlined,
    QuestionCircleOutlined,
    RocketOutlined,
    SmileOutlined,
} from "@ant-design/icons";
import type { TourProps } from "antd";
import { Button, Card, Flex, Form, Input, notification, Popover, Radio, Space, Tour } from "antd";
import { useForm } from "antd/es/form/Form";
import { addDoc, collection } from "firebase/firestore";
import { useLocation, useSearchParams } from "react-router";
import LinkButton from "../LinkButton";

function useOutsideClick<T extends HTMLElement>(callback: () => void) {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                callback();
            }
        };

        document.addEventListener("click", handleClick, true);

        return () => {
            document.removeEventListener("click", handleClick, true);
        };
    }, [callback]);

    return ref;
}

type Overall = "negative" | "neutral" | "positive";

type Feedback = "bug" | "feature" | "general";

type FormData = {
    overall: Overall;
    leaveFeedback: boolean;
    feedbackType: Feedback;
    comment: string;
};

async function submitFeedback(feedbackData: {
    overall: Overall;
    location: string;
    feedbackType?: Feedback;
    comment?: string;
    uid?: string;
    email?: string;
}) {
    const db = getFirestoreDB();
    const { overall, location, feedbackType, comment, uid, email } = feedbackData;

    const docData: Record<string, unknown> = {
        overall,
        location,
        date: new Date(),
    };
    if (feedbackType !== undefined) {
        if (comment !== undefined) {
            docData.feedbackType = feedbackType;
        } else {
            throw new Error("Feedback type selected, but no feedback was given");
        }
        docData.comment = comment;
    }
    if (uid !== undefined) {
        docData.uid = uid;
    }
    if (email !== undefined) {
        docData.email = email;
    }

    return addDoc(collection(db, "trialFinderFeedback"), docData);
}

function BugReportingForm(props: { handleExit: (status: "success" | "failure" | "closed") => void }) {
    const [form] = useForm<FormData>();
    const { user } = useFirebaseAuth();
    const location = useLocation();
    const { handleExit } = props;

    const [isSendingForm, setIsSendingForm] = useState(false);

    const handleFinish = async () => {
        const validatedFields = await form.validateFields();
        console.log(validatedFields);

        setIsSendingForm(true);
        try {
            const formData = validatedFields;
            console.log(formData);
            await submitFeedback({
                overall: formData.overall,
                location: location.pathname,
                feedbackType: formData.feedbackType,
                comment: formData.comment,
                uid: user?.uid,
                email: user?.email || undefined,
            });
        } catch (error) {
            console.error(error);
            handleExit("failure");
        }
        handleExit("success");
    };

    return (
        <Card
            className="float-menu"
            title="Let us know your thoughts"
            extra={<Button type="text" icon={<CloseOutlined />} onClick={() => handleExit("closed")} />}
        >
            <Form layout="vertical" requiredMark={false} size="large" form={form} onFinish={handleFinish}>
                <Form.Item
                    name="overall"
                    label="How would you rate your overall experience?"
                    rules={[
                        {
                            required: true,
                            message: "Please rate your experience",
                        },
                    ]}
                >
                    <Radio.Group
                        className="overall-options"
                        options={[
                            { value: "negative", label: <FrownOutlined /> },
                            { value: "neutral", label: <MehOutlined /> },
                            { value: "positive", label: <SmileOutlined /> },
                        ]}
                    />
                </Form.Item>
                <Form.Item name="leaveFeedback" label="Would you like to leave a comment?">
                    <Radio.Group
                        options={[
                            { value: true, label: "Yes" },
                            { value: false, label: "No" },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.leaveFeedback !== currentValues.leaveFeedback
                    }
                >
                    {() =>
                        form.getFieldValue("leaveFeedback") && (
                            <>
                                <Form.Item
                                    name="feedbackType"
                                    label="What kind of comment is this?"
                                    rules={[
                                        {
                                            required: true,
                                            message: "What kind of comment is this?",
                                        },
                                    ]}
                                >
                                    <Radio.Group
                                        className="feedback-type-options"
                                        options={[
                                            {
                                                value: "feature",
                                                label: (
                                                    <Space>
                                                        <RocketOutlined />
                                                        New Feature
                                                    </Space>
                                                ),
                                            },
                                            {
                                                value: "bug",
                                                label: (
                                                    <Space>
                                                        <BugOutlined /> Bug
                                                    </Space>
                                                ),
                                            },
                                            {
                                                value: "general",
                                                label: (
                                                    <Space>
                                                        <CommentOutlined />
                                                        General feedback
                                                    </Space>
                                                ),
                                            },
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="comment"
                                    label="What would you like to tell us?"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please write your message here",
                                        },
                                    ]}
                                >
                                    <Input.TextArea />
                                </Form.Item>
                            </>
                        )
                    }
                </Form.Item>
                <Form.Item>
                    <Flex justify="end" gap={8}>
                        <Button onClick={() => handleExit("closed")}>Close</Button>
                        <Button type="primary" htmlType="submit" loading={isSendingForm}>
                            Submit
                        </Button>
                    </Flex>
                </Form.Item>
            </Form>
        </Card>
    );
}

// We want a named function for the ref
const CobbSurvey = forwardRef(function CobbSurvey(props, ref: React.ForwardedRef<HTMLAnchorElement>) {
    const { user } = useFirebaseAuth();
    const prefilled = user?.email ? `referredEmail=${user.email}` : "";
    return (
        <a
            target="_blank"
            rel="noopener noreferrer"
            href={`https://form.jotform.com/250825424296157?${prefilled}`}
            className="float-button float-button-large"
            onClick={() => BetaSurveyPopupDismissed.set("true")}
            ref={ref}
        >
            <Flex vertical gap={4} align="center">
                <img height="35px" src={cobbLogo} alt="Cobb Institute Logo." />
                <div>Survey</div>
            </Flex>
        </a>
    );
});

export default function FloatMenu() {
    const [api, contextHolder] = notification.useNotification();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isTourOpen, setIsTourOpen] = useState(() => getBooleanUrlFlag("tour"));
    const [dismissedSurvey, setDismissedSurvey] = useState(BetaSurveyDismissed.get() === "true");
    const [floatOffsets, setFloatOffsets] = useState({ bottom: 30, right: 30 });
    const { userProfile } = useFirebaseAuth();
    const floatRef = useRef<HTMLDivElement>(null);

    const isTestUser = userProfile?.inUserTesting || false;
    const { userOrganization } = useUserOrganization();

    const isCobb = userOrganization.key === "cobb";

    const [searchParams, setSearchParams] = useSearchParams();

    const closeWhenOutsideRef = useOutsideClick<HTMLDivElement>(() => setIsMenuOpen(false));

    const tourRef1 = useRef(null);
    const tourRef2 = useRef(null);
    const tourRef3 = useRef(null);

    const steps: TourProps["steps"] = [
        {
            title: "Leave a comment",
            description: "If you encounter a bug, please report it by using the form here.",
            target: () => tourRef2.current,
            placement: "left",
        },
    ];

    if (isTestUser)
        steps.push({
            title: "View Instructions",
            description: "You can access the testing instructions here at any time.",
            target: () => tourRef1.current,
        });

    if (isCobb)
        steps.push({
            title: "Take the Cobb Survey",
            description: "When you finish using the site, please take a moment to complete a feedback survey.",
            target: () => tourRef3.current,
        });

    const handleFormExit = (status: "success" | "failure" | "closed") => {
        api.destroy();
        if (status === "success") {
            api.success({
                title: "Thanks for sharing your feedback!",
                description: "We'll use it to make ResearchLinQ even better.",
                placement: "bottom",
            });
        }
        setIsMenuOpen(false);
    };

    const setFloatElementRef = (node: HTMLDivElement | null) => {
        floatRef.current = node;
        closeWhenOutsideRef.current = node;
    };

    useEffect(() => {
        const compactViewportBreakpoint = 576;
        const desktopBottomOffset = 30;
        const desktopRightOffset = 30;
        const mobileBottomOffset = 16;
        const mobileRightOffset = 16;
        const footerGap = 16;
        const chatbotGap = 24;
        const actionsGap = 16;
        const minimumHorizontalClearance = 420;

        const getViewportDefaults = () =>
            window.innerWidth <= compactViewportBreakpoint
                ? { bottom: mobileBottomOffset, right: mobileRightOffset }
                : { bottom: desktopBottomOffset, right: desktopRightOffset };

        const isVisibleInViewport = (rect: DOMRect) =>
            rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;

        const doRectsOverlap = (
            first: { top: number; right: number; bottom: number; left: number },
            second: { top: number; right: number; bottom: number; left: number }
        ) =>
            first.left < second.right &&
            first.right > second.left &&
            first.top < second.bottom &&
            first.bottom > second.top;

        const getFooterBottomOffset = () => {
            const { bottom: defaultBottomOffset } = getViewportDefaults();

            if (window.innerWidth <= compactViewportBreakpoint) {
                return defaultBottomOffset;
            }

            const footer = document.querySelector<HTMLElement>(".footer");
            if (!footer) {
                return defaultBottomOffset;
            }

            const footerRect = footer.getBoundingClientRect();
            const footerVisibleHeight = window.innerHeight - footerRect.top;

            if (footerVisibleHeight <= 0) {
                return defaultBottomOffset;
            }

            return Math.round(defaultBottomOffset + footerVisibleHeight + footerGap);
        };

        const updateFloatOffsets = () => {
            const viewportDefaults = getViewportDefaults();
            let bottom = getFooterBottomOffset();
            let right = viewportDefaults.right;
            const chatbot = document.querySelector<HTMLElement>(".chatbot-container");

            if (chatbot) {
                const chatbotRect = chatbot.getBoundingClientRect();
                const hasHorizontalClearance = chatbotRect.left >= minimumHorizontalClearance;

                if (hasHorizontalClearance) {
                    right = Math.round(window.innerWidth - chatbotRect.left + chatbotGap);
                } else {
                    bottom = Math.round(Math.max(bottom, window.innerHeight - chatbotRect.top + chatbotGap));
                }
            }

            const floatRect = floatRef.current?.getBoundingClientRect();
            const searchActions = document.querySelector<HTMLElement>(".sticky-buttons.active .search-form-actions");

            if (floatRect && searchActions) {
                const searchActionsRect = searchActions.getBoundingClientRect();

                if (isVisibleInViewport(searchActionsRect)) {
                    const projectedFloatRect = {
                        top: window.innerHeight - bottom - floatRect.height,
                        right: window.innerWidth - right,
                        bottom: window.innerHeight - bottom,
                        left: window.innerWidth - right - floatRect.width,
                    };

                    if (doRectsOverlap(projectedFloatRect, searchActionsRect)) {
                        bottom = Math.round(Math.max(bottom, window.innerHeight - searchActionsRect.top + actionsGap));
                    }
                }
            }

            const nextOffsets = { bottom, right };

            setFloatOffsets((previous) =>
                previous.bottom === nextOffsets.bottom && previous.right === nextOffsets.right ? previous : nextOffsets
            );
        };

        const handleViewportChange = () => {
            window.requestAnimationFrame(updateFloatOffsets);
        };

        const observer = new MutationObserver(handleViewportChange);
        observer.observe(document.body, { childList: true, subtree: true });

        updateFloatOffsets();
        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange);
        };
    }, []);

    // Each element needs to be wrapped in a div to prevent them from shrinking
    return (
        <div
            ref={setFloatElementRef}
            className="float"
            style={{ bottom: `${floatOffsets.bottom}px`, right: `${floatOffsets.right}px` }}
        >
            {contextHolder}
            <div className="float-menu-container">{isMenuOpen && <BugReportingForm handleExit={handleFormExit} />}</div>
            <div className="float-button-container">
                {isTestUser && (
                    <div>
                        <a
                            ref={tourRef1}
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://drive.google.com/file/d/1M2qRxAZzBE3B-WkXF24mWPG4GNkw_cSo/view?usp=drive_link"
                            className="float-button"
                        >
                            <FileOutlined />
                        </a>
                    </div>
                )}
                <div>
                    <button
                        ref={tourRef2}
                        type="button"
                        className="float-button"
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                    >
                        <QuestionCircleOutlined />
                    </button>
                </div>
                {userOrganization.surveyUrl && (
                    <Popover
                        title="Take Survey"
                        open={!dismissedSurvey}
                        content={
                            <Flex vertical gap={16} align="flex-end">
                                <div>Would you take a quick survey to help us improve the Trial Finder?</div>
                                <Space>
                                    <Button
                                        onClick={() => {
                                            BetaSurveyDismissed.set("true");
                                            setDismissedSurvey(true);
                                        }}
                                    >
                                        Dismiss
                                    </Button>
                                    <LinkButton
                                        onClick={() => {
                                            BetaSurveyDismissed.set("true");
                                            setDismissedSurvey(true);
                                        }}
                                        type="primary"
                                        to={userOrganization.surveyUrl}
                                    >
                                        Take Survey
                                    </LinkButton>
                                </Space>
                            </Flex>
                        }
                    >
                        <a
                            href={userOrganization.surveyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="float-button"
                        >
                            <CommentOutlined />
                        </a>
                    </Popover>
                )}
                {isCobb && (
                    <div>
                        <CobbSurvey ref={tourRef3} />
                    </div>
                )}
            </div>
            <Tour
                steps={steps}
                open={isTourOpen}
                onClose={() => {
                    clearUrlFlag("tour");
                    setIsTourOpen(false);
                    setSearchParams((params) => {
                        params.set("tour", "false");
                        return params;
                    });
                }}
            />
        </div>
    );
}
